import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Trash,
  PencilSimple,
  X,
  CircleNotch,
  FilePdf,
  Upload,
  FloppyDisk,
  CurrencyCircleDollar,
  Car,
  CreditCard,
  ListChecks,
  Receipt,
} from '@phosphor-icons/react';
import axios from '../../utils/axiosInstance';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const VAT_RATE = 0.23;

const MONTHS_PL = [
  'styczeń', 'luty', 'marzec', 'kwiecień', 'maj', 'czerwiec',
  'lipiec', 'sierpień', 'wrzesień', 'październik', 'listopad', 'grudzień',
];

const PERIODS = ['01-15', '16-30'];

const TYPE_META = {
  leasing: { label: 'Leasing', cls: 'bg-muted text-blue-800 border-border', icon: Car },
  kredyt: { label: 'Kredyt', cls: 'bg-amber-100 text-amber-800 border-amber-300', icon: CreditCard },
  dpd: { label: 'DPD', cls: 'bg-rose-100 text-rose-800 border-rose-300', icon: Receipt },
  inne: { label: 'Inne', cls: 'bg-slate-100 text-slate-700 border-slate-300', icon: ListChecks },
};

const fmtPLN = (n) =>
  (Number(n) || 0).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' zł';

// === Single specyfikacja row ==========================================

function SpecRow({ idx, spec, onChange, onDelete, onReupload, uploading, uploadProgress, uploadElapsed }) {
  const netto = parseFloat(spec.wynagrodzenie_netto) || 0;
  const vat = netto * VAT_RATE;
  const brutto = netto + vat;
  return (
    <div className="border border-gray-200 rounded-lg p-3 bg-white" data-testid={`spec-row-${idx}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold text-slate-500 uppercase">
          Specyfikacja #{idx + 1}
          {spec.okres_label && (
            <span className="ml-2 font-normal normal-case text-slate-400 font-mono">
              {spec.okres_label}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <label className="cursor-pointer inline-flex items-center gap-1 px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded text-[10px] font-semibold text-slate-600"
            title="Wgraj ponownie PDF do tej specyfikacji">
            {uploading ? <CircleNotch className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
            {uploading
              ? (uploadProgress < 100 ? `${uploadProgress}%` : `OCR ${uploadElapsed}s`)
              : 'PDF'}
            <input
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={onReupload}
              disabled={uploading}
              data-testid={`spec-reupload-${idx}`}
            />
          </label>
          <button
            onClick={onDelete}
            className="text-red-500 hover:bg-red-50 rounded p-1"
            title="Usuń specyfikację"
            data-testid={`spec-delete-${idx}`}
          >
            <Trash className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {spec.pdf_filename && (
        <div className="mb-2 text-[10px] text-slate-500 flex items-center gap-1 truncate">
          <FilePdf className="w-3 h-3 shrink-0" /> {spec.pdf_filename}
        </div>
      )}
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div>
          <label className="block text-[10px] text-slate-500 uppercase font-semibold">Netto</label>
          <input
            type="number"
            step="0.01"
            value={spec.wynagrodzenie_netto ?? ''}
            onChange={(e) => onChange({ ...spec, wynagrodzenie_netto: e.target.value })}
            className="w-full mt-0.5 px-2 py-1 border border-gray-300 rounded text-sm font-mono"
            data-testid={`spec-netto-${idx}`}
          />
        </div>
        <div>
          <label className="block text-[10px] text-slate-500 uppercase font-semibold">Dopłata</label>
          <input
            type="number"
            step="0.01"
            value={spec.doplata_paliwowa ?? ''}
            onChange={(e) => onChange({ ...spec, doplata_paliwowa: e.target.value })}
            className="w-full mt-0.5 px-2 py-1 border border-gray-300 rounded text-sm font-mono"
            data-testid={`spec-doplata-${idx}`}
          />
        </div>
        <div>
          <label className="block text-[10px] text-slate-500 uppercase font-semibold">Obciążenia</label>
          <input
            type="number"
            step="0.01"
            value={spec.noty ?? ''}
            onChange={(e) => onChange({ ...spec, noty: e.target.value })}
            className="w-full mt-0.5 px-2 py-1 border border-gray-300 rounded text-sm font-mono"
            data-testid={`spec-noty-${idx}`}
          />
        </div>
      </div>
      <div className="mt-1.5 flex justify-end text-[10px] text-slate-500 font-mono">
        <span>Brutto (z VAT): <span className="font-bold text-emerald-700">{fmtPLN(brutto)}</span></span>
      </div>
    </div>
  );
}

// === Period card =====================================================

function PeriodCard({ year, month, period, data, dpdTotal = 0, onSaved }) {
  const [specs, setSpecs] = useState(data?.specyfikacje || []);
  const [notyDod, setNotyDod] = useState(data?.noty_dodatkowe ?? 0);
  const [note, setNote] = useState(data?.note ?? '');
  const [dirty, setDirty] = useState(false);
  const [uploadingIdx, setUploadingIdx] = useState(-1); // -1=none, -2=new spec
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadElapsed, setUploadElapsed] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setSpecs(data?.specyfikacje || []);
    setNotyDod(data?.noty_dodatkowe ?? 0);
    setNote(data?.note ?? '');
    setDirty(false);
  }, [data]);

  // Aggregated sums for this period
  const sumNetto = specs.reduce((s, sp) => s + (parseFloat(sp.wynagrodzenie_netto) || 0), 0);
  const sumDoplata = specs.reduce((s, sp) => s + (parseFloat(sp.doplata_paliwowa) || 0), 0);
  const sumNoty = specs.reduce((s, sp) => s + (parseFloat(sp.noty) || 0), 0);
  const vat = sumNetto * VAT_RATE;
  const brutto = sumNetto + vat;
  const notyDodNum = parseFloat(notyDod) || 0;
  const przelew = brutto - sumNoty - dpdTotal - notyDodNum;

  const runOcrUpload = async (file) => {
    if (file.size > 30 * 1024 * 1024) {
      alert('Plik za duży (>30 MB). Skompresuj go i spróbuj ponownie.');
      return null;
    }
    setUploadProgress(0);
    setUploadElapsed(0);
    const startedAt = Date.now();
    const tick = setInterval(() => {
      setUploadElapsed(Math.round((Date.now() - startedAt) / 1000));
    }, 500);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const callOcr = async () =>
        axios.post(`${API}/dochod/extract-pdf`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 45000,
          onUploadProgress: (evt) => {
            if (evt.total) setUploadProgress(Math.round((evt.loaded * 100) / evt.total));
          },
        });
      let ocrResp;
      try {
        ocrResp = await callOcr();
      } catch (firstErr) {
        const retriable =
          firstErr.response?.status === 504 ||
          firstErr.response?.status === 502 ||
          firstErr.code === 'ECONNABORTED' ||
          firstErr.message === 'Network Error';
        if (!retriable) throw firstErr;
        await new Promise((r) => setTimeout(r, 1500));
        ocrResp = await callOcr();
      }
      return { ocr: ocrResp.data, file };
    } catch (err) {
      let msg = err.response?.data?.detail || err.message;
      const status = err.response?.status;
      if (status === 504 || status === 502) {
        msg = 'Serwer OCR jest przeciążony. Spróbuj wgrać plik za 10 sekund jeszcze raz.';
      } else if (err.code === 'ECONNABORTED' || /timeout/i.test(err.message)) {
        msg = 'Czas oczekiwania przekroczony. Spróbuj jeszcze raz lub zmniejsz plik.';
      } else if (err.message === 'Network Error') {
        msg = 'Błąd sieci — sprawdź połączenie i spróbuj ponownie.';
      }
      alert('Błąd OCR: ' + msg);
      return null;
    } finally {
      clearInterval(tick);
      setUploadProgress(0);
      setUploadElapsed(0);
    }
  };

  const handleAddSpecFromPdf = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingIdx(-2);
    const res = await runOcrUpload(file);
    setUploadingIdx(-1);
    if (!res) return;
    const { ocr } = res;
    setSpecs((prev) => [
      ...prev,
      {
        wynagrodzenie_netto: ocr.wynagrodzenie_netto ?? 0,
        doplata_paliwowa: ocr.doplata_paliwowa ?? 0,
        noty: ocr.noty ?? 0,
        okres_label: ocr.okres_label ?? null,
        pdf_filename: ocr.filename || file.name,
        note: null,
      },
    ]);
    setDirty(true);
    const allEmpty =
      ocr.wynagrodzenie_netto == null &&
      ocr.doplata_paliwowa == null &&
      ocr.noty == null &&
      !ocr.okres_label;
    if (allEmpty) {
      setTimeout(() => alert('OCR nie wydobył żadnych danych. Wpisz wartości ręcznie.'), 100);
    }
  };

  const handleReuploadPdf = async (idx, e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingIdx(idx);
    const res = await runOcrUpload(file);
    setUploadingIdx(-1);
    if (!res) return;
    const { ocr } = res;
    setSpecs((prev) =>
      prev.map((s, i) =>
        i === idx
          ? {
              ...s,
              wynagrodzenie_netto: ocr.wynagrodzenie_netto ?? s.wynagrodzenie_netto,
              doplata_paliwowa: ocr.doplata_paliwowa ?? s.doplata_paliwowa,
              noty: ocr.noty ?? s.noty,
              okres_label: ocr.okres_label ?? s.okres_label,
              pdf_filename: ocr.filename || file.name,
            }
          : s,
      ),
    );
    setDirty(true);
  };

  const handleAddEmptySpec = () => {
    setSpecs((prev) => [...prev, { wynagrodzenie_netto: 0, doplata_paliwowa: 0, noty: 0 }]);
    setDirty(true);
  };

  const handleSpecChange = (idx, newSpec) => {
    setSpecs((prev) => prev.map((s, i) => (i === idx ? newSpec : s)));
    setDirty(true);
  };

  const handleSpecDelete = (idx) => {
    if (!window.confirm(`Usunąć specyfikację #${idx + 1}?`)) return;
    setSpecs((prev) => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const handleSave = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/dochod/period`, {
        year,
        month,
        period,
        specyfikacje: specs.map((s) => ({
          id: s.id || null,
          wynagrodzenie_netto: parseFloat(s.wynagrodzenie_netto) || 0,
          doplata_paliwowa: parseFloat(s.doplata_paliwowa) || 0,
          noty: parseFloat(s.noty) || 0,
          okres_label: s.okres_label || null,
          pdf_filename: s.pdf_filename || null,
          note: s.note || null,
        })),
        noty_dodatkowe: parseFloat(notyDod) || 0,
        note: note || null,
      });
      setDirty(false);
      onSaved && onSaved();
    } catch (err) {
      alert('Błąd zapisu: ' + (err.response?.data?.detail || err.message));
    } finally {
      setBusy(false);
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm(`Wyczyścić dane okresu ${period}?`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/dochod/period`, { params: { year, month, period } });
      setSpecs([]);
      setNotyDod(0);
      setNote('');
      setDirty(false);
      onSaved && onSaved();
    } finally {
      setBusy(false);
    }
  };

  const addingNew = uploadingIdx === -2;

  return (
    <div
      className="bg-white rounded-sm shadow-sm border border-gray-200 overflow-hidden"
      data-testid={`period-card-${period}`}
    >
      <div className="bg-muted text-white px-5 py-3 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-200">Okres</div>
          <div className="text-xl font-bold">{period}</div>
        </div>
        <div className="text-right text-sm text-slate-200">
          {specs.length > 0 ? `${specs.length} specyfikacj${specs.length === 1 ? 'a' : 'e'}` : 'brak specyfikacji'}
        </div>
      </div>

      <div className="p-5 space-y-4">
        {/* Specyfikacje list */}
        <div className="space-y-2">
          {specs.map((sp, idx) => (
            <SpecRow
              key={idx}
              idx={idx}
              spec={sp}
              onChange={(ns) => handleSpecChange(idx, ns)}
              onDelete={() => handleSpecDelete(idx)}
              onReupload={(e) => handleReuploadPdf(idx, e)}
              uploading={uploadingIdx === idx}
              uploadProgress={uploadProgress}
              uploadElapsed={uploadElapsed}
            />
          ))}
          {specs.length === 0 && !addingNew && (
            <div className="text-center py-4 text-xs text-slate-400 border border-dashed border-gray-200 rounded-lg">
              Brak specyfikacji. Wgraj PDF lub dodaj pustą.
            </div>
          )}
        </div>

        {/* Add spec buttons */}
        <div className="flex gap-2">
          <label
            className="cursor-pointer flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition"
          >
            {addingNew ? (
              <CircleNotch className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" weight="bold" />
            )}
            {addingNew
              ? uploadProgress < 100
                ? `Wysyłanie ${uploadProgress}%`
                : `Analizowanie OCR... ${uploadElapsed}s`
              : 'Dodaj specyfikację (wgraj PDF)'}
            <input
              data-testid={`upload-pdf-${period}`}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={handleAddSpecFromPdf}
              disabled={addingNew}
            />
          </label>
          <button
            onClick={handleAddEmptySpec}
            className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-bold flex items-center gap-1"
            title="Dodaj pustą specyfikację (bez OCR)"
            data-testid={`add-empty-spec-${period}`}
          >
            <Plus className="w-4 h-4" weight="bold" />
          </button>
        </div>

        {/* Period sums */}
        <div className="bg-slate-50 rounded-xl p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Suma netto ({specs.length} spec.)
            </span>
            <span className="text-xl font-bold text-slate-800 font-mono">{fmtPLN(sumNetto)}</span>
          </div>
          <div className="text-sm text-slate-600 flex items-center justify-between">
            <span>+ VAT 23%</span>
            <span className="font-mono">{fmtPLN(vat)}</span>
          </div>
          <div className="border-t border-slate-300 pt-2 flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-700">= Brutto razem</span>
            <span className="text-2xl font-bold text-emerald-600 font-mono">{fmtPLN(brutto)}</span>
          </div>
        </div>

        {/* Dopłata paliwowa (sum, read-only) */}
        <div className="flex items-center justify-between px-2">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
            Dopłata paliwowa (razem)
          </span>
          <span className="text-lg font-semibold text-orange-600 font-mono" data-testid={`sum-doplata-${period}`}>
            {fmtPLN(sumDoplata)}
          </span>
        </div>

        {/* Obciążenia (sum, read-only) */}
        <div className="flex items-center justify-between px-2">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
            Obciążenia z dokumentów
          </span>
          <span className="text-lg font-semibold text-rose-600 font-mono" data-testid={`sum-noty-${period}`}>
            −{fmtPLN(sumNoty)}
          </span>
        </div>

        {/* Noty dodatkowe (period-level manual) */}
        <div>
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
            Noty dodatkowe (ręcznie)
          </label>
          <input
            data-testid={`input-noty-dod-${period}`}
            type="number"
            step="0.01"
            value={notyDod}
            onChange={(e) => { setNotyDod(e.target.value); setDirty(true); }}
            className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
          />
        </div>

        {/* DPD read-only */}
        <div>
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
            Faktury DPD (z kosztów, okres {period})
          </label>
          <div className="text-lg font-semibold text-rose-600 mt-1 font-mono" data-testid={`dpd-total-${period}`}>
            −{fmtPLN(dpdTotal)}
          </div>
        </div>

        {/* Przelew (computed) */}
        <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-4">
          <div className="text-xs uppercase font-bold text-emerald-700 tracking-wider">
            Przelew (do otrzymania)
          </div>
          <div className="text-xs text-emerald-600 mt-1">
            Brutto − Obciążenia − DPD − Noty dodatkowe
          </div>
          <div
            className={`text-3xl font-bold font-mono mt-2 ${przelew >= 0 ? 'text-emerald-700' : 'text-red-600'}`}
            data-testid={`przelew-${period}`}
          >
            {fmtPLN(przelew)}
          </div>
        </div>

        {/* Save / Clear */}
        <div className="flex items-center gap-2 pt-2">
          <button
            data-testid={`save-period-${period}`}
            onClick={handleSave}
            disabled={busy || !dirty}
            className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? <CircleNotch className="w-4 h-4 animate-spin" /> : <FloppyDisk className="w-4 h-4" weight="bold" />}
            {dirty ? 'Zapisz zmiany' : 'Zapisano'}
          </button>
          {(specs.length > 0 || notyDodNum > 0) && (
            <button
              data-testid={`clear-period-${period}`}
              onClick={handleClearAll}
              disabled={busy}
              className="px-3 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-sm"
              title="Wyczyść cały okres"
            >
              <Trash className="w-4 h-4" weight="bold" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// === Cost modal ======================================================

function KosztModal({ year, month, sources, koszt, onClose, onSaved }) {
  const isEdit = !!koszt;
  const [form, setForm] = useState({
    type: koszt?.type || 'leasing',
    name: koszt?.name || '',
    amount: koszt?.amount ?? '',
    pojazd_id: koszt?.pojazd_id || '',
    cykliczny: koszt?.cykliczny ?? true,
    period: koszt?.period || '',
    note: koszt?.note || '',
  });
  const [busy, setBusy] = useState(false);

  const handleTypeChange = (newType) => {
    setForm((f) => ({
      ...f,
      type: newType,
      pojazd_id: '',
      name: newType === 'inne' || newType === 'dpd' ? f.name : '',
      period: newType === 'dpd' ? (f.period || '01-15') : '',
    }));
  };

  const handleSelectPojazd = (id) => {
    const p = sources.pojazdy.find((x) => x.id === id);
    if (!p) {
      setForm((f) => ({ ...f, pojazd_id: '', name: '' }));
      return;
    }
    const label = [p.nazwa, p.nr_rejestracyjny].filter(Boolean).join(' • ');
    setForm((f) => ({ ...f, pojazd_id: id, name: label }));
  };

  const handleSelectKredyt = (id) => {
    const k = sources.kredyty.find((x) => x.id === id);
    if (!k) {
      setForm((f) => ({ ...f, name: '', amount: '' }));
      return;
    }
    setForm((f) => ({
      ...f,
      name: k.name,
      amount: k.current_rata > 0 ? k.current_rata : f.amount,
    }));
  };

  const handleSubmit = async () => {
    if (!form.name || !form.amount) {
      alert('Wypełnij nazwę i kwotę');
      return;
    }
    if (form.type === 'dpd' && !form.period) {
      alert('Wybierz okres (01-15 lub 16-30) dla faktury DPD');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        type: form.type,
        name: form.name,
        amount: parseFloat(form.amount) || 0,
        pojazd_id: form.pojazd_id || null,
        cykliczny: !!form.cykliczny,
        period: form.type === 'dpd' ? (form.period || null) : null,
        note: form.note || null,
      };
      if (isEdit) {
        await axios.put(`${API}/dochod/koszty/${koszt.id}`, payload);
      } else {
        await axios.post(`${API}/dochod/koszty`, { year, month, ...payload });
      }
      onSaved();
    } catch (err) {
      alert('Błąd: ' + (err.response?.data?.detail || err.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="koszt-modal">
      <div className="bg-white rounded-sm border border-border max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-xl font-bold text-slate-800">
            {isEdit ? 'Edytuj koszt' : 'Dodaj koszt'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" weight="bold" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {/* Type buttons */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase mb-2 block">
              Typ kosztu
            </label>
            <div className="grid grid-cols-4 gap-2">
              {Object.entries(TYPE_META).map(([k, m]) => {
                const Icon = m.icon;
                const active = form.type === k;
                return (
                  <button
                    key={k}
                    type="button"
                    data-testid={`koszt-type-${k}`}
                    onClick={() => handleTypeChange(k)}
                    className={`px-3 py-3 rounded-lg border-2 text-sm font-semibold flex flex-col items-center gap-1 transition ${
                      active ? `${m.cls} border-current` : 'bg-white text-slate-600 border-gray-200 hover:bg-slate-50'
                    }`}
                  >
                    <Icon className="w-5 h-5" weight={active ? 'fill' : 'regular'} />
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Leasing → pojazd dropdown */}
          {form.type === 'leasing' && (
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
                Pojazd (z listy)
              </label>
              <select
                data-testid="koszt-pojazd-select"
                value={form.pojazd_id}
                onChange={(e) => handleSelectPojazd(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">— Wybierz pojazd —</option>
                {sources.pojazdy.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nazwa}{p.nr_rejestracyjny ? ` (${p.nr_rejestracyjny})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Kredyt → corporate credit dropdown */}
          {form.type === 'kredyt' && sources.kredyty.length > 0 && (
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
                Kredyt firmowy (z Zobowiązań — przeniesie ratę)
              </label>
              <select
                data-testid="koszt-kredyt-select"
                onChange={(e) => handleSelectKredyt(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                defaultValue=""
              >
                <option value="">— Wybierz kredyt —</option>
                {sources.kredyty.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.name}{k.current_rata > 0 ? ` — ${fmtPLN(k.current_rata)}/mies.` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* DPD → period selector */}
          {form.type === 'dpd' && (
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
                Okres przelewu (faktura DPD odejmie się od przelewu tego okresu)
              </label>
              <div className="grid grid-cols-2 gap-2">
                {['01-15', '16-30'].map((p) => (
                  <button
                    key={p}
                    type="button"
                    data-testid={`koszt-period-${p}`}
                    onClick={() => setForm({ ...form, period: p })}
                    className={`px-3 py-2 rounded-lg border-2 text-sm font-semibold transition ${
                      form.period === p
                        ? 'border-rose-500 bg-rose-100 text-rose-800'
                        : 'border-slate-200 text-slate-600 hover:border-rose-300'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Name */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
              Nazwa
            </label>
            <input
              data-testid="koszt-name"
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={form.type === 'leasing' ? 'BMW X5' : form.type === 'kredyt' ? 'Kredyt obrotowy' : 'np. ZUS'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>

          {/* Amount */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
              Kwota (zł)
            </label>
            <input
              data-testid="koszt-amount"
              type="number"
              step="0.01"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
            />
          </div>

          {/* Cykliczny */}
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input
              data-testid="koszt-cykliczny"
              type="checkbox"
              checked={form.cykliczny}
              onChange={(e) => setForm({ ...form, cykliczny: e.target.checked })}
              className="w-4 h-4 accent-emerald-600"
            />
            <span>
              <strong>Cykliczny</strong> — automatycznie skopiuj do następnego miesiąca
            </span>
          </label>

          {/* Note */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
              Notatka (opcjonalnie)
            </label>
            <input
              type="text"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-200 bg-slate-50">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg text-sm font-semibold"
          >
            Anuluj
          </button>
          <button
            data-testid="koszt-save"
            onClick={handleSubmit}
            disabled={busy}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-bold flex items-center gap-2 disabled:opacity-50"
          >
            {busy ? <CircleNotch className="w-4 h-4 animate-spin" /> : <FloppyDisk className="w-4 h-4" weight="bold" />}
            {isEdit ? 'Zapisz zmiany' : 'Dodaj'}
          </button>
        </div>
      </div>
    </div>
  );
}

// === Main tab ========================================================

export default function DochodFirmyTab() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const [periodData, setPeriodData] = useState({ '01-15': null, '16-30': null });
  const [dpdByPeriod, setDpdByPeriod] = useState({ '01-15': 0, '16-30': 0 });
  const [tankPaliwo, setTankPaliwo] = useState(0);
  const [tankByPojazd, setTankByPojazd] = useState([]);
  const [wynagrodzenia, setWynagrodzenia] = useState({ '01-15': 0, '16-30': 0, total: 0 });
  const [podatki, setPodatki] = useState({ zus: 0, cit: 0, vat: 0 });
  const [podatkiDraft, setPodatkiDraft] = useState({ zus: '', cit: '', vat: '' });
  const [savingPodatki, setSavingPodatki] = useState(false);
  const [koszty, setKoszty] = useState([]);
  const [sources, setSources] = useState({ pojazdy: [], kredyty: [] });
  const [loading, setLoading] = useState(false);
  const [showKosztModal, setShowKosztModal] = useState(false);
  const [editingKoszt, setEditingKoszt] = useState(null);

  const [showDebug, setShowDebug] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);

  const runDebug = async () => {
    setShowDebug(true);
    setDebugInfo({ status: 'loading' });
    try {
      const [pojazdyResp, sourcesResp, periodResp] = await Promise.all([
        axios.get(`${API}/pojazdy`).catch((e) => ({ error: e.message, status: e.response?.status })),
        axios.get(`${API}/dochod/sources`).catch((e) => ({ error: e.message, status: e.response?.status })),
        axios.get(`${API}/dochod/period`, { params: { year, month } }).catch((e) => ({
          error: e.message, status: e.response?.status,
        })),
      ]);
      setDebugInfo({
        api_url: API,
        token_present: !!localStorage.getItem('access_token'),
        token_prefix: (localStorage.getItem('access_token') || '').slice(0, 12) + '...',
        pojazdy_count: Array.isArray(pojazdyResp.data) ? pojazdyResp.data.length : `ERR: ${pojazdyResp.error || 'n/a'}`,
        pojazdy_sample: Array.isArray(pojazdyResp.data) ? pojazdyResp.data.slice(0, 2).map((p) => ({ id: p.id, nazwa: p.nazwa })) : null,
        sources_pojazdy_count: sourcesResp.data?.pojazdy?.length ?? `ERR: ${sourcesResp.error || 'n/a'}`,
        sources_kredyty_count: sourcesResp.data?.kredyty?.length ?? '—',
        period_by_pojazd_count: periodResp.data?.tankowania_by_pojazd?.length ?? `ERR: ${periodResp.error || 'n/a'}`,
        period_paliwo_total: periodResp.data?.tankowania_paliwo_total,
      });
    } catch (e) {
      setDebugInfo({ error: e.message });
    }
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [period, kosztyResp, srcResp] = await Promise.all([
        axios.get(`${API}/dochod/period`, { params: { year, month } }),
        axios.get(`${API}/dochod/koszty`, { params: { year, month } }),
        axios.get(`${API}/dochod/sources`),
      ]);
      setPeriodData(period.data.periods || { '01-15': null, '16-30': null });
      setDpdByPeriod(period.data.dpd_by_period || { '01-15': 0, '16-30': 0 });
      setTankPaliwo(period.data.tankowania_paliwo_included || 0);
      setTankByPojazd(period.data.tankowania_by_pojazd || []);
      setWynagrodzenia(period.data.wynagrodzenia || { '01-15': 0, '16-30': 0, total: 0 });
      const pod = period.data.podatki || { zus: 0, cit: 0, vat: 0 };
      setPodatki(pod);
      setPodatkiDraft({
        zus: pod.zus ? String(pod.zus) : '',
        cit: pod.cit ? String(pod.cit) : '',
        vat: pod.vat ? String(pod.vat) : '',
      });
      setKoszty(kosztyResp.data || []);
      setSources(srcResp.data || { pojazdy: [], kredyty: [] });
    } catch (err) {
      console.error('fetch dochod failed', err);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleDeleteKoszt = async (id) => {
    if (!window.confirm('Usunąć koszt?')) return;
    await axios.delete(`${API}/dochod/koszty/${id}`);
    fetchAll();
  };

  const handleSavePodatki = async () => {
    setSavingPodatki(true);
    try {
      await axios.put(`${API}/dochod/podatki`, {
        year,
        month,
        zus: parseFloat(podatkiDraft.zus) || 0,
        cit: parseFloat(podatkiDraft.cit) || 0,
        vat: parseFloat(podatkiDraft.vat) || 0,
      });
      fetchAll();
    } catch (err) {
      alert('Błąd zapisu podatków: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSavingPodatki(false);
    }
  };

  const handleToggleDolicz = async (pojazdId, checked) => {
    // Optimistic UI: update local state immediately, then persist
    setTankByPojazd((prev) =>
      prev.map((p) => (p.pojazd_id === pojazdId ? { ...p, dolicz_do_doplaty: checked } : p)),
    );
    setTankPaliwo((prev) => {
      const row = tankByPojazd.find((p) => p.pojazd_id === pojazdId);
      if (!row) return prev;
      return checked ? prev + row.kwota : prev - row.kwota;
    });
    try {
      await axios.patch(`${API}/dochod/pojazd/${pojazdId}/dolicz`, {
        dolicz_do_doplaty: checked,
      });
    } catch (err) {
      console.error('toggle failed', err);
      fetchAll(); // revert if backend rejected
    }
  };

  // === Calculations ===
  const sumNetto = ['01-15', '16-30'].reduce(
    (s, p) => s + (parseFloat(periodData[p]?.wynagrodzenie_netto) || 0), 0,
  );
  const sumVat = sumNetto * VAT_RATE;
  const sumBrutto = sumNetto + sumVat;
  const sumPaliwowa = ['01-15', '16-30'].reduce(
    (s, p) => s + (parseFloat(periodData[p]?.doplata_paliwowa) || 0), 0,
  );
  const sumNoty = ['01-15', '16-30'].reduce(
    (s, p) => s + (parseFloat(periodData[p]?.noty) || 0), 0,
  );
  const sumNotyDod = ['01-15', '16-30'].reduce(
    (s, p) => s + (parseFloat(periodData[p]?.noty_dodatkowe) || 0), 0,
  );
  const sumDpd = (dpdByPeriod['01-15'] || 0) + (dpdByPeriod['16-30'] || 0);
  const sumPrzelew = sumBrutto - sumNoty - sumDpd - sumNotyDod;
  const paliwoDiff = sumPaliwowa - tankPaliwo;
  const kosztyTotal = koszty.reduce((s, k) => s + (parseFloat(k.amount) || 0), 0);
  const wynTotal = parseFloat(wynagrodzenia.total) || 0;
  const podatkiTotal = (podatki.zus || 0) + (podatki.cit || 0) + (podatki.vat || 0);
  const dochodNetto = sumBrutto - kosztyTotal - wynTotal - podatkiTotal;
  const podatkiDirty =
    (parseFloat(podatkiDraft.zus) || 0) !== (podatki.zus || 0) ||
    (parseFloat(podatkiDraft.cit) || 0) !== (podatki.cit || 0) ||
    (parseFloat(podatkiDraft.vat) || 0) !== (podatki.vat || 0);

  const yearOptions = Array.from({ length: 6 }, (_, i) => now.getFullYear() - 3 + i);

  return (
    <div className="space-y-6" data-testid="dochod-firmy-tab">
      {/* Header */}
      <div className="bg-white rounded-sm shadow-sm border border-gray-200 p-5">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <CurrencyCircleDollar className="w-6 h-6 text-emerald-600" weight="bold" />
              Dochód firmy
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Rozliczenie miesięczne — 2 okresy + stałe koszty
            </p>
          </div>
          <div className="ml-auto flex items-end gap-2">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Rok</label>
              <select
                data-testid="dochod-year"
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value, 10))}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Miesiąc</label>
              <select
                data-testid="dochod-month"
                value={month}
                onChange={(e) => setMonth(parseInt(e.target.value, 10))}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm min-w-[140px]"
              >
                {MONTHS_PL.map((m, i) => (
                  <option key={i + 1} value={i + 1}>{m}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="mt-3 text-right">
          <button
            data-testid="dochod-debug-btn"
            onClick={runDebug}
            className="text-xs text-slate-400 hover:text-foreground underline"
          >
            🔧 Diagnostyka API
          </button>
        </div>
      </div>

      {/* Debug panel */}
      {showDebug && (
        <div className="bg-yellow-50 border-2 border-yellow-300 rounded-sm p-4 text-xs font-mono text-slate-800">
          <div className="flex items-center justify-between mb-2">
            <strong className="text-yellow-900">Diagnostyka — co widzi Twoja przeglądarka:</strong>
            <button onClick={() => setShowDebug(false)} className="text-yellow-900">✕</button>
          </div>
          <pre className="whitespace-pre-wrap break-all overflow-x-auto">
            {JSON.stringify(debugInfo, null, 2)}
          </pre>
        </div>
      )}

      {/* Period cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {PERIODS.map((p) => (
          <PeriodCard
            key={p}
            year={year}
            month={month}
            period={p}
            data={periodData[p]}
            dpdTotal={dpdByPeriod[p] || 0}
            onSaved={fetchAll}
          />
        ))}
        {/* Razem (3rd col) */}
        <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white rounded-md shadow-md p-5 flex flex-col justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-emerald-100">Razem za miesiąc</div>
            <div className="text-2xl font-bold mt-1">{MONTHS_PL[month - 1]} {year}</div>
          </div>
          <div className="space-y-3 mt-4">
            <div>
              <div className="text-xs text-emerald-100 uppercase">Wynagrodzenie netto</div>
              <div className="text-2xl font-bold font-mono">{fmtPLN(sumNetto)}</div>
            </div>
            <div>
              <div className="text-xs text-emerald-100 uppercase">VAT 23%</div>
              <div className="text-lg font-mono">{fmtPLN(sumVat)}</div>
            </div>
            <div className="border-t border-emerald-300/50 pt-3">
              <div className="text-xs text-emerald-100 uppercase">Brutto</div>
              <div className="text-3xl font-bold font-mono">{fmtPLN(sumBrutto)}</div>
            </div>
            <div className="border-t border-emerald-300/50 pt-3 space-y-1.5 text-sm">
              <div className="flex justify-between text-emerald-100">
                <span>− Noty</span>
                <span className="font-mono">{fmtPLN(sumNoty)}</span>
              </div>
              <div className="flex justify-between text-emerald-100">
                <span>− Faktury DPD</span>
                <span className="font-mono">{fmtPLN(sumDpd)}</span>
              </div>
              <div className="flex justify-between text-emerald-100">
                <span>− Noty dodatkowe</span>
                <span className="font-mono">{fmtPLN(sumNotyDod)}</span>
              </div>
              <div className="border-t border-emerald-300/50 pt-2 mt-2">
                <div className="text-xs text-emerald-100 uppercase">Przelew razem</div>
                <div className="text-2xl font-bold font-mono" data-testid="przelew-razem">
                  {fmtPLN(sumPrzelew)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Paliwo comparison */}
      <div className="bg-white rounded-sm shadow-sm border border-gray-200 p-5">
        <h3 className="text-lg font-bold text-slate-800 mb-3">Paliwo — porównanie</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="text-xs uppercase font-semibold text-orange-700">Dopłata z dokumentów</div>
            <div className="text-2xl font-bold text-orange-700 font-mono mt-1">
              {fmtPLN(sumPaliwowa)}
            </div>
          </div>
          <div className="bg-muted border border-border rounded-lg p-4">
            <div className="text-xs uppercase font-semibold text-foreground">
              Tankowania (zaznaczone)
            </div>
            <div className="text-2xl font-bold text-foreground font-mono mt-1">
              {fmtPLN(tankPaliwo)}
            </div>
          </div>
          <div className={`rounded-lg p-4 border ${paliwoDiff >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
            <div className={`text-xs uppercase font-semibold ${paliwoDiff >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
              Różnica (dopłata − paliwo)
            </div>
            <div className={`text-2xl font-bold font-mono mt-1 ${paliwoDiff >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
              {paliwoDiff >= 0 ? '+' : ''}{fmtPLN(paliwoDiff)}
            </div>
          </div>
        </div>

        {/* Vehicle selection checklist — always visible if pojazdy exist */}
        {tankByPojazd.length > 0 ? (
          <div className="mt-4 border border-gray-200 rounded-xl overflow-hidden">
            <div className="bg-slate-50 px-4 py-2 flex items-center justify-between text-xs font-semibold text-slate-600 uppercase">
              <span>Pojazdy zaliczane do dopłaty</span>
              <span className="text-slate-400 normal-case">
                Odznacz auta, na które dopłata nie obowiązuje
              </span>
            </div>
            <ul className="divide-y divide-gray-100">
              {tankByPojazd.map((p) => {
                const checked = p.dolicz_do_doplaty !== false;
                const hasFuel = p.kwota > 0;
                return (
                  <li
                    key={p.pojazd_id}
                    className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-slate-50 transition ${
                      !checked ? 'opacity-50' : ''
                    }`}
                    onClick={() => handleToggleDolicz(p.pojazd_id, !checked)}
                  >
                    <input
                      data-testid={`paliwo-include-${p.pojazd_id}`}
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleToggleDolicz(p.pojazd_id, e.target.checked);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="w-4 h-4 accent-emerald-600 cursor-pointer"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-800 text-sm">{p.nazwa || '—'}</div>
                      {p.nr_rejestracyjny && (
                        <div className="text-xs text-slate-500 font-mono">{p.nr_rejestracyjny}</div>
                      )}
                    </div>
                    <div className="text-right text-sm">
                      {hasFuel ? (
                        <>
                          <div className="font-mono font-bold text-slate-800">{fmtPLN(p.kwota)}</div>
                          {p.litry > 0 && (
                            <div className="text-xs text-slate-500 font-mono">{p.litry.toFixed(2)} L</div>
                          )}
                        </>
                      ) : (
                        <span className="text-xs text-slate-400">brak tankowań</span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : (
          <div className="mt-4 text-center text-sm text-slate-400 py-3 border border-dashed border-gray-200 rounded-xl">
            Brak pojazdów. Dodaj je w zakładce "Pojazdy", aby móc je tu odznaczać.
          </div>
        )}
      </div>

      {/* Koszty */}
      <div className="bg-white rounded-sm shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-bold text-slate-800">Stałe koszty miesiąca</h3>
            <p className="text-xs text-slate-500">
              Cykliczne pozycje są automatycznie kopiowane na następny miesiąc.
            </p>
          </div>
          <button
            data-testid="add-koszt-btn"
            onClick={() => { setEditingKoszt(null); setShowKosztModal(true); }}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-bold flex items-center gap-2"
          >
            <Plus className="w-4 h-4" weight="bold" /> Dodaj koszt
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center text-slate-400">
            <CircleNotch className="w-6 h-6 animate-spin inline" />
          </div>
        ) : koszty.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">
            Brak kosztów dla tego miesiąca. Kliknij „Dodaj koszt".
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-bold uppercase text-slate-600">Typ</th>
                  <th className="text-left py-3 px-4 text-xs font-bold uppercase text-slate-600">Nazwa</th>
                  <th className="text-right py-3 px-4 text-xs font-bold uppercase text-slate-600">Kwota</th>
                  <th className="text-center py-3 px-4 text-xs font-bold uppercase text-slate-600">Cykliczny</th>
                  <th className="text-left py-3 px-4 text-xs font-bold uppercase text-slate-600">Notatka</th>
                  <th className="text-right py-3 px-4 text-xs font-bold uppercase text-slate-600">Akcje</th>
                </tr>
              </thead>
              <tbody>
                {koszty.map((k) => {
                  const meta = TYPE_META[k.type] || TYPE_META.inne;
                  const isAutoSynced = !!(k.credit_id || k.leasing_id);
                  const autoSyncLabel = k.credit_id
                    ? "kredyt firmowy"
                    : k.leasing_id
                      ? "leasing"
                      : "";
                  return (
                    <tr key={k.id} className="border-t border-gray-100 hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold border ${meta.cls}`}>
                          {meta.label}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-semibold text-slate-800">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span>{k.name}</span>
                          {k.type === 'dpd' && k.period && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                              okres {k.period}
                            </span>
                          )}
                          {isAutoSynced && (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200"
                              title="Automatycznie zsynchronizowano z harmonogramem kredytu"
                              data-testid={`koszt-autosync-badge-${k.id}`}
                            >
                              auto · harmonogram
                            </span>
                          )}
                          {isAutoSynced && k.paid && (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200"
                              title="Rata oznaczona jako opłacona w harmonogramie kredytu"
                              data-testid={`koszt-paid-badge-${k.id}`}
                            >
                              ✓ opłacono
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right font-mono font-bold text-slate-800">
                        {fmtPLN(k.amount)}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {k.cykliczny ? (
                          <span className="text-emerald-600">✓</span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-500">{k.note || '—'}</td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {isAutoSynced ? (
                            <span
                              className="text-xs text-slate-400 px-2"
                              title="Edytuj w zakładce Zobowiązania"
                            >
                              {autoSyncLabel}
                            </span>
                          ) : (
                            <>
                              <button
                                data-testid={`edit-koszt-${k.id}`}
                                onClick={() => { setEditingKoszt(k); setShowKosztModal(true); }}
                                className="p-2 text-foreground hover:bg-muted rounded-lg"
                              >
                                <PencilSimple className="w-4 h-4" weight="bold" />
                              </button>
                              <button
                                data-testid={`delete-koszt-${k.id}`}
                                onClick={() => handleDeleteKoszt(k.id)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                              >
                                <Trash className="w-4 h-4" weight="bold" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                <tr className="border-t-2 border-slate-300 bg-slate-50">
                  <td colSpan={2} className="py-3 px-4 text-right text-sm font-bold text-slate-700">
                    Łącznie koszty:
                  </td>
                  <td className="py-3 px-4 text-right font-mono font-bold text-lg text-red-600">
                    {fmtPLN(kosztyTotal)}
                  </td>
                  <td colSpan={3}></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Wynagrodzenia (z Do wypłaty) */}
      <div className="bg-white rounded-sm shadow-sm border border-gray-200 p-5">
        <h3 className="text-lg font-bold text-slate-800 mb-3">
          Wynagrodzenia <span className="text-xs text-slate-500 font-normal">(z "Do wypłaty")</span>
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-slate-50 border border-gray-200 rounded-lg p-4">
            <div className="text-xs uppercase font-semibold text-slate-600">Okres 01-15</div>
            <div className="text-2xl font-bold text-slate-800 font-mono mt-1">
              {fmtPLN(wynagrodzenia['01-15'])}
            </div>
          </div>
          <div className="bg-slate-50 border border-gray-200 rounded-lg p-4">
            <div className="text-xs uppercase font-semibold text-slate-600">Okres 16-30</div>
            <div className="text-2xl font-bold text-slate-800 font-mono mt-1">
              {fmtPLN(wynagrodzenia['16-30'])}
            </div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="text-xs uppercase font-semibold text-red-700">Razem za miesiąc</div>
            <div className="text-2xl font-bold text-red-700 font-mono mt-1">
              {fmtPLN(wynTotal)}
            </div>
          </div>
        </div>
      </div>

      {/* Podatki */}
      <div className="bg-white rounded-sm shadow-sm border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-bold text-slate-800">Podatki</h3>
          {podatkiDirty && (
            <button
              data-testid="save-podatki"
              onClick={handleSavePodatki}
              disabled={savingPodatki}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-bold flex items-center gap-2 disabled:opacity-50"
            >
              {savingPodatki ? <CircleNotch className="w-4 h-4 animate-spin" /> : <FloppyDisk className="w-4 h-4" weight="bold" />}
              Zapisz
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { key: 'zus', label: 'ZUS' },
            { key: 'cit', label: 'CIT' },
            { key: 'vat', label: 'VAT' },
          ].map(({ key, label }) => (
            <div key={key} className="bg-slate-50 border border-gray-200 rounded-lg p-4">
              <label className="text-xs uppercase font-semibold text-slate-600 block">{label}</label>
              <input
                data-testid={`podatek-${key}`}
                type="number"
                step="0.01"
                value={podatkiDraft[key]}
                onChange={(e) => setPodatkiDraft({ ...podatkiDraft, [key]: e.target.value })}
                placeholder="0,00"
                className="w-full mt-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-lg font-mono"
              />
              {podatki[key] > 0 && (
                <div className="text-xs text-slate-500 mt-1">
                  Zapisane: {fmtPLN(podatki[key])}
                </div>
              )}
            </div>
          ))}
          <div className="sm:col-span-3 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center justify-between">
            <span className="text-sm font-bold text-red-700 uppercase">Razem podatki</span>
            <span className="text-2xl font-bold text-red-700 font-mono">{fmtPLN(podatkiTotal)}</span>
          </div>
        </div>
      </div>

      {/* Bottom summary — Dochód netto firmy */}
      <div className="bg-muted text-white rounded-sm shadow-lg p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-300">
              Dochód netto firmy ({MONTHS_PL[month - 1]} {year})
            </div>
            <div className="text-sm text-slate-400 mt-1 leading-relaxed">
              Brutto {fmtPLN(sumBrutto)}
              {' − '}Koszty {fmtPLN(kosztyTotal)}
              {' − '}Wynagr. {fmtPLN(wynTotal)}
              {' − '}Podatki {fmtPLN(podatkiTotal)}
            </div>
          </div>
          <div className={`text-4xl font-bold font-mono ${dochodNetto >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {dochodNetto >= 0 ? '' : '−'}{fmtPLN(Math.abs(dochodNetto))}
          </div>
        </div>
      </div>

      {showKosztModal && (
        <KosztModal
          year={year}
          month={month}
          sources={sources}
          koszt={editingKoszt}
          onClose={() => setShowKosztModal(false)}
          onSaved={() => { setShowKosztModal(false); fetchAll(); }}
        />
      )}
    </div>
  );
}
